import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import SendbirdChat, { SendbirdChatParams, User } from '@sendbird/chat';
import { GroupChannelModule, SendbirdGroupChat } from '@sendbird/chat/groupChannel';
import { environment } from 'src/environments/environment';

/**
 * SbInstanceService is reposible for managing sendbird instance and logged in user.
 * For mobile its also communicating the app's state change to sendBird.
 */
@Injectable({
  providedIn: 'root',
})
export class SbInstanceService {
  // Sendbird instance
  private sendBirdInstance: SendbirdGroupChat;

  // Function called before disconnecting the user from sendbird. Called before disconnecting the user.
  private onDisconnectUnregisterNotificationFn: () => Promise<void>;

  /**
   * Returns the sendbird instance.
   */
  get instance(): SendbirdGroupChat {
    return this.sendBirdInstance;
  }

  get currentUser(): User {
    return this.sendBirdInstance.currentUser;
  }

  /**
   * The function called before disconnecting the user from sendbird.
   */
  set addUnregisterPushNotificationFn(unregisterFn: () => Promise<void>) {
    this.onDisconnectUnregisterNotificationFn = unregisterFn;
  }

  constructor(private platform: Platform) {
    // Created sendbird instance
    const params: SendbirdChatParams<[GroupChannelModule]> = {
      appId: environment.sendBirdAppId,
      modules: [new GroupChannelModule()],
    };
    this.sendBirdInstance = SendbirdChat.init(params) as SendbirdGroupChat;

    this.addStateHandler();
  }

  /**
   * Communicates the app's state change to sendBird.
   * Background and foreground states are used in notification and online status logic.
   */
  addStateHandler(): void {
    // Setting user online status
    this.platform.ready().then(() => {
      this.platform.resume.subscribe(() => {
        this.sendBirdInstance.setForegroundState();
      });
      this.platform.pause.subscribe(() => {
        this.sendBirdInstance.setBackgroundState();
      });
    });
  }

  /**
   * Disconnect the current user from sendbird. Additionally, this will unregister
   * from the Sendbird Push notification service.
   */
  async disconnectUser(): Promise<void> {
    if (this.onDisconnectUnregisterNotificationFn) {
      await this.onDisconnectUnregisterNotificationFn();
    }

    await this.sendBirdInstance.disconnect();
  }
}
